import { StreetOrientationMode } from "building-types/building/streetOrientationMode";
import type { ElectricalSlots } from "building-types/electrical/electricalSlots";
import { LoadType } from "building-types/electrical/electricalTypesEnum";
import { BuiltInExteriorPropId } from "building-types/props/builtInExteriorPropId";
import { PropId } from "building-types/props/propIds";
import type { HomeSettings } from "building-types/settings/homeSettings";
import type { PorchSettings } from "building-types/settings/porchSettings";
import type { RearPorchSettings } from "building-types/settings/rearPorchSettings";
import { SettingsType } from "building-types/settings/settingsType";
import {
  ColonnadeTerminationType,
  WallIntersectionType,
  ShutterType,
  StoolCasingType,
} from "building-types/settings/settingsTypesEnum";
import { defaultExteriorPropCatalog } from "../elements/propCatalog/exteriorPropCatalog";
import { defaultElectricalSettings } from "./builtinElectricalSettings";
import { BuiltinHomeSettingsId, builtinHomeSettingsPrettyName } from "./builtinHomeSettingsId";
import {
  defaultExteriorWindowCasingSettings,
  defaultHomeSettings,
  defaultPorchSettings,
  defaultRearPorchSettings,
  defaultWainscotingSettings,
  defaultWindowMullionSettings,
} from "./defaultSettings";

const southernPorchSettings: PorchSettings = {
  ...defaultPorchSettings,
  maxColumnSpacing: 90,
  doorPathColumnSpread: 60,
  baseColumnOffset: true,
};

const rearPorchSettings: RearPorchSettings = {
  ...defaultRearPorchSettings,
  maxColumnSpacing: 240,
  doorPathColumnSpread: 60,
  colonnadeTerminationRules: [
    {
      endType: WallIntersectionType.TEE,
      terminationType: ColonnadeTerminationType.NO_COLUMN,
    },
  ],
  baseColumnOffset: true,
};

const defaultBuilderElectricalSlots: ElectricalSlots = {
  ...defaultElectricalSettings.slots,
  loads: {
    ...defaultElectricalSettings.slots.loads,
    primaryLight: LoadType.RECESSED_CEILING_LIGHT,
    secondaryLight: LoadType.MOUNTED_CEILING_LIGHT,
  },
};

const defaultBuilderSettings: { [id: string]: HomeSettings } = {
  [BuiltinHomeSettingsId.Default_01]: {
    ...defaultHomeSettings,
    name: builtinHomeSettingsPrettyName[BuiltinHomeSettingsId.Default_01],
    exteriorMaterials: {
      type: SettingsType.EXTERIOR_MATERIAL_SETTINGS,
      id: BuiltinHomeSettingsId.Default_01 + "_exterior",
      palettes: [BuiltinHomeSettingsId.Default_01],
    },
    exteriorProps: {
      type: SettingsType.EXTERIOR_PROP_SETTINGS,
      propTypes: {
        ...defaultExteriorPropCatalog,
        garageDoorSingle: {
          ...defaultExteriorPropCatalog[BuiltInExteriorPropId.GARAGEDOORSINGLE],
          propAssetId: PropId.DoorGar_S8x7_2x4Pattern_H_01,
        },
        garageDoorDouble: {
          ...defaultExteriorPropCatalog[BuiltInExteriorPropId.GARAGEDOORDOUBLE],
          propAssetId: PropId.DoorGar_D16x7_2x4Pattern_H_01,
        },
        doorSingleGlass: {
          ...defaultExteriorPropCatalog[BuiltInExteriorPropId.DOORSINGLEGLASS],
          propAssetId: PropId.DoorExt_Single36_QuarterSolid4_02,
        },
        doorSingleGlassLow: {
          ...defaultExteriorPropCatalog[BuiltInExteriorPropId.DOORSINGLEGLASSLOW],
          propAssetId: PropId.DoorExt_Single36_QuarterSolid4_01,
        },
      },
    },
    roof: {
      ...defaultHomeSettings.roof,
      shedOverhang: 12,
      rakeBracketPropId: BuiltInExteriorPropId.RAKEBRACKET,
      rakeBracketParams: [48],
      rakeBracketAtPeak: true,
      friezeBoardOrientation: StreetOrientationMode.FRONT_FACES,
      generateRoofFraming: false,
    },
    dormer: {
      ...defaultHomeSettings.dormer,
      eaveReturnDistance: 6,
    },
    wainscoting: {
      ...defaultWainscotingSettings,
      height: 23.99,
      thickness: 2.5,
      wainscotingMode: StreetOrientationMode.FRONT_AND_SIDE_FACES,
    },
    porch: southernPorchSettings,
    rearPorch: rearPorchSettings,

    exteriorWindowCasing: {
      ...defaultExteriorWindowCasingSettings,
      stoolCasingType: StoolCasingType.DISABLED,
      stoolCasingDepth: 2,
      apronHeight: 3.5,
      casingOrientation: StreetOrientationMode.FRONT_FACES,
    },
    windowMullions: {
      ...defaultWindowMullionSettings,
      paneTwoXCount: 2,
    },
    windowShutters: {
      type: SettingsType.WINDOW_SHUTTER_SETTINGS,
      shutterType: ShutterType.DISABLED,
    },
    electrical: { ...defaultElectricalSettings, slots: defaultBuilderElectricalSlots },
  },
};

export default defaultBuilderSettings;
